import { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flag } from "../Flag";

const initials = (name = "") => name.split(/[\s&/-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export function OrgLogo({ name, domain, className = "", onFail }) {
  const [step, setStep] = useState(0);
  const srcs = [`${process.env.REACT_APP_BACKEND_URL}/api/orgs/logo/${domain}?name=${encodeURIComponent(name)}`, `https://icons.duckduckgo.com/ip3/${domain}.ico`];
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (step >= srcs.length) onFail?.(domain); }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  // A source that neither loads nor errors within 8s (slow CDN / blocked) is skipped so tiles never sit empty.
  useEffect(() => {
    if (step >= srcs.length) return;
    setLoaded(false);
    const t = setTimeout(() => { if (!(ref.current?.complete && ref.current.naturalWidth > 32)) setStep((n) => n + 1); }, 8000);
    return () => clearTimeout(t);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  if (step >= srcs.length) return <span className={`grid place-items-center rounded-lg bg-primary/10 font-display text-xs font-semibold text-primary ${className}`} aria-label={name} data-testid="org-monogram">{initials(name)}</span>;
  return <img ref={ref} src={srcs[step]} alt={name} title={name} loading="eager" decoding="async" className={`object-contain transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"} ${className}`} onError={() => setStep((n) => n + 1)} onLoad={(e) => { if (e.currentTarget.naturalWidth <= 32) setStep((n) => n + 1); else setLoaded(true); }} />;
}

// Prefer short names for the 2×2 logo hero so labels never get cut; fall back to longer ones only when needed.
export const heroOrgs = (items, skip = new Set()) => { const ok = items.filter(([, d]) => !skip.has(d)); const short = ok.filter(([n]) => n.length <= 12); return [...short, ...ok.filter((x) => !short.includes(x))].slice(0, 4); };

// Fit " · "-separated parts into a character budget; never ellipsize, drop parts and say "+N" instead.
export function fitParts(parts, budget = 34) {
  const list = (Array.isArray(parts) ? parts : String(parts ?? "").split(" · ")).filter(Boolean);
  if (!list.length) return "";
  const out = [list[0]];
  for (const p of list.slice(1)) { const rest = list.length - out.length - 1; if (out.join(" · ").length + 3 + p.length + (rest ? 4 : 0) <= budget) out.push(p); else break; }
  const more = list.length - out.length;
  return out.join(" · ") + (more ? ` +${more}` : "");
}
export const orgsLine = (orgs, budget) => fitParts(orgs.items.map(([n]) => n), budget);

export function LogoStrip({ orgs, fast, testId }) {
  const [off, setOff] = useState(0);
  const n = orgs.items.length;
  useEffect(() => {
    if (n <= 3) return;
    const t = setInterval(() => setOff((o) => (o + 1) % n), fast ? 1600 : 3600);
    return () => clearInterval(t);
  }, [n, fast]);
  const shown = Array.from({ length: Math.min(3, n) }, (_, i) => orgs.items[(off + i) % n]);
  return (
    <span className="absolute bottom-3 right-3 flex items-center" data-testid={testId} data-offset={off}>
      <AnimatePresence mode="popLayout" initial={false}>
        {shown.map(([name, dom], i) => (
          orgs.kind === "flag" ? (
            <motion.span key={dom} layout title={name} aria-label={name} initial={{ opacity: 0, scale: 0.6, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.6, y: -6 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className={`grid h-8 w-8 place-items-center rounded-full bg-background shadow-card ring-2 ring-background ${i ? "-ml-2" : ""}`} style={{ zIndex: 3 - i }}><Flag flag={dom} size={20} /></motion.span>
          ) : (
          <motion.span key={dom} layout initial={{ opacity: 0, scale: 0.6, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.6, y: -6 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-background p-1 shadow-card ring-2 ring-background ${i ? "-ml-2" : ""}`} style={{ zIndex: 3 - i }}><OrgLogo name={name} domain={dom} className="h-full w-full rounded-full" /></motion.span>
          )
        ))}
      </AnimatePresence>
      <span className={`ml-1.5 whitespace-nowrap rounded-full bg-background/95 px-2 py-0.5 font-semibold text-foreground shadow-card ${shown[0][0].length > 16 ? "text-xs" : "text-xs"}`} data-testid={`${testId}-current`}>{shown[0][0]}</span>
    </span>
  );
}
